import {
  AttributeTypeEnum,
  FormEngineModeTypeEnum,
  ObjectEnum,
  ViewTypeEnum,
} from '@celito.clients/enums';
import {
  useLayout,
  useObjectDefinition,
  useQueryParams,
  useView,
} from '@celito.clients/hooks';
import { getRecordDetailApi } from '@celito.clients/services';
import { Loader, useBreadcrumbs } from '@celito.clients/shared';
import {
  LayoutConfiguration,
  LayoutRulesDataSchema,
  OwnerType,
  Task,
} from '@celito.clients/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { Field, Section } from 'libs/core/src/types/wizard-config';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import * as yup from 'yup';

import { FormEngineContextProvider } from '../../context';
import { WizardStateStatus } from '../../enums/wizard-enum';
import { useNonEmptyPrevious } from '../../hooks';
import { useLayoutRules } from '../../hooks/useLayoutRules';
import { getLifeCycleStageGroups } from '../../utils/attribute-config';
import { getFormEngineDefaultValues } from '../../utils/helper';
import { generateYupSchemaFromLayoutRules } from '../../utils/validator-generator';
import {
  ApiValidationError,
  FieldApiError,
  FormWizardComponentProps,
  WizardStepState,
} from './form-wizard.model';
import classes from './form-wizard.module.css';
import FormWizardView from './form-wizard.view';

export const FormWizardController = (props: FormWizardComponentProps) => {
  const { configureLayout, pageTitle } = useLayout();
  const { replaceLastBreadcrumb } = useBreadcrumbs();

  const [step, setStep] = useState(0);
  const [wizardState, setWizardState] = useState<string>(
    WizardStateStatus.UNSUBMITTED
  );
  const [sections, setSections] = useState<Section[]>();
  const [fieldsApiErrors, setFieldsApiErrors] = useState<ApiValidationError>(
    {} as ApiValidationError
  );
  const [recordData, setRecordData] = useState<Record<string, unknown>>({});
  const [wizardStepsState, setWizardStepsState] = useState<WizardStepState[]>(
    []
  );
  const [isAllSectionsValid, setIsAllSectionsValid] = useState<boolean>(false);

  const [task, setTask] = useState<Task>();
  const [shouldRefetchTask, setShouldRefetchTask] = useState<boolean>(false);
  const [validationSchema, setValidationSchema] = useState<
    yup.ObjectSchema<yup.AnyObject>
  >({} as yup.ObjectSchema<yup.AnyObject>);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onSubmit',
    mode: 'all',
  });

  const navigate = useNavigate();
  const { getSearchParams } = useQueryParams();
  const searchParams = getSearchParams();
  const { mode } = useParams();
  const requestType = searchParams?.requestType;

  const { data: viewDtoResponse, isLoading: isWizardConfigLoading } = useView({
    view: props.viewName!,
    queryOptions: {
      enabled: !!props.viewName,
    },
  });

  const hasTask = task && Object.keys(task)?.length > 0;

  const viewConfig = viewDtoResponse?.viewDto;
  const view = viewConfig?.view[0];
  const objectName = view?.objectName;

  const {
    data: objectDefinition,
    isLoading: isAttributeConfigLoading,
    refetch: refetchObjectDefinition,
  } = useObjectDefinition({
    objectName: (objectName ?? '') as ObjectEnum,
    recordName: props.recordName,
    version: (recordData?.version as string) ?? searchParams?.version,
    resolveDefaultValues: true,
    queryOptions: {
      enabled: !!objectName,
    },
  });

  // when version changes objectDefinition will be refetched thereby
  // becoming undefined. During this time, we use the previous objectDefinition
  const prevObjectDefinition = useNonEmptyPrevious(objectDefinition);

  const layoutType = view?.pageLayout;
  const totalSteps = sections?.length || 0;
  const submitUrl = view?.submitRedirectUrl || '';
  const editRouteLink = view?.editViewName;
  const listViewName = view?.listViewName;
  const showCancelPopup = view?.showCancelPopup;
  const labelFormat = objectDefinition?.labelFormat;
  const progressTabs =
    getLifeCycleStageGroups(
      objectDefinition!,
      recordData?.lifecycleStage as string
    ) ?? [];

  const {
    trigger,
    formState: { isDirty, errors },
    watch,
    reset,
    setError,
    getValues,
    setValue,
    clearErrors,
  } = methods;
  const formData = watch();
  // using stringified version to avoid multiple re-renders
  const formDataStringified = JSON.stringify(formData);
  const sectionsStringified = JSON.stringify(sections);
  const schemaStringified = JSON.stringify(validationSchema);

  const getSectionsFields = useMemo(
    () => (sections: Section[] | undefined) => {
      let allfields: Field[] = [];
      for (const section of sections || []) {
        const fields = section.fields;
        allfields = allfields.concat(fields);
      }
      return allfields;
    },
    [formDataStringified]
  );

  const { fieldsState, setFieldsState } = useLayoutRules(formData, {
    fields: getSectionsFields(sections ?? []),
  });

  const fieldsStateStringified = JSON.stringify(fieldsState);
  const dirtyFields = Object.keys(methods.formState.dirtyFields);

  const objDefinition = prevObjectDefinition ?? objectDefinition;
  const result: Record<string, { column: string; result: string[] }> = {};
  const filterReference = objDefinition?.objectAttributeDefinitions.filter(
    (attr) =>
      attr.dataType === AttributeTypeEnum.Reference &&
      attr.ownerType !== OwnerType.System
  );

  filterReference?.forEach(({ columnName, relationship }) => {
    const recordValue = recordData[columnName];
    if (recordValue && relationship?.objectName) {
      const isArray = Array.isArray(recordValue);
      const record = isArray ? recordValue[0] : recordValue;
      const columnType = record?.name ? 'name' : 'recordName';

      result[relationship.objectName] = {
        column: columnType,
        result: isArray
          ? recordValue.map((record) => record[columnType])
          : [(recordValue as Record<string, string>)[columnType]],
      };
    }
  });

  // Additional filter for objectRecordName attributes
  const filterObjectRecordName =
    objDefinition?.objectAttributeDefinitions.filter(
      (attr) => attr.columnName === 'objectRecordName'
    );

  for (const { columnName } of filterObjectRecordName || []) {
    const recordValue = recordData[columnName];
    if (recordValue) {
      const isArray = Array.isArray(recordValue);
      const record = isArray ? recordValue[0] : recordValue;
      const objectName = record?.objectName;

      if (objectName) {
        const columnType = record?.name ? 'name' : 'recordName';

        result[objectName] = {
          column: columnType,
          result: isArray
            ? recordValue.map((record) => record[columnType])
            : [(recordValue as Record<string, string>)[columnType]],
        };
      }
    }
  }

  useEffect(() => {
    const fieldsStateParsed = JSON.parse(fieldsStateStringified) as Record<
      string,
      LayoutRulesDataSchema
    >;

    const newSchema = generateYupSchemaFromLayoutRules(
      watch(),
      sections?.flatMap((section) => section.fields) ?? [],
      objectDefinition?.objectValidationRules ?? [],
      objectDefinition?.objectAttributeDefinitions ?? [],
      fieldsStateParsed
    );

    if (
      newSchema &&
      typeof newSchema === 'object' &&
      Object.keys(newSchema).length > 0
    ) {
      setValidationSchema(newSchema);
    }
  }, [
    formDataStringified,
    fieldsStateStringified,
    watch,
    sectionsStringified,
    step,
    objectDefinition?.objectAttributeDefinitions,
  ]);

  useEffect(() => {
    if (props.mode === FormEngineModeTypeEnum.Create && objectDefinition) {
      Object.keys(getValues())?.forEach((field) => {
        if (requestType && field !== 'initiation_type__a') {
          setValue(field, null);
        }
      });
      // Set new default values for the form
      reset({
        ...getValues(),
        ...getFormEngineDefaultValues(objectDefinition),
      });
      setRecordData({});
    }
  }, [objectDefinition?.name, getValues, reset, props.mode]);

  useEffect(() => {
    setStep(0);
  }, [props.mode]);

  useEffect(() => {
    if (view?.label && !requestType) {
      const enablePadding = !mode;

      const layoutConfig: LayoutConfiguration = {
        pageTitle: enablePadding ? pageTitle : '',
        headerTitle: view?.label ?? '',
        enablePadding,
      };

      if (!enablePadding) {
        layoutConfig.showHeadingLoader =
          props.mode === FormEngineModeTypeEnum.Edit ? true : undefined;
      }

      configureLayout(layoutConfig);
    }

    if (view?.sections && view.sections.length > 0) {
      setWizardStepsState(
        Array.from({ length: view.sections.length }).map((_, idx) => ({
          number: idx,
          isValid: undefined,
          isDirty: false,
          hasNavigated: false,
          hasBeenDirtyAndSaved: false,
        }))
      );
      setSections(view.sections ?? []);
    }
  }, [view?.sections.length]);

  const onStepClick = async (index: number) => {
    // validate the current step and advance.
    // for custom screens, validation is done at the custom screen level
    if (
      !sections?.[step]?.pageComponent &&
      props.mode !== FormEngineModeTypeEnum.View
    ) {
      const currentStepFields = (sections?.[step]?.fields ?? []).map(
        (field) => field.columnName
      );

      // validate only current section fields
      try {
        const isValid = await trigger(currentStepFields);
        setWizardStepsState((prev) =>
          prev.map((prevStep) =>
            prevStep.number === step ? { ...prevStep, isValid } : prevStep
          )
        );
      } catch (error) {
        // Handle error if needed
      }
    }

    setStep(index);
  };

  const validateStep = async (step: number) => {
    if (
      (viewDtoResponse?.viewDto?.viewType as ViewTypeEnum) !== ViewTypeEnum.View
    ) {
      //validate the current step
      const currentStepFields = (sections?.[step]?.fields ?? []).map(
        (field) => field.columnName
      );
      const isValid = await trigger(currentStepFields);
      setWizardStepsState((prev) =>
        prev.map((prevStep) =>
          prevStep.number === step ? { ...prevStep, isValid } : prevStep
        )
      );
    }
  };

  const goToPreviousStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (step > 0 && step <= totalSteps) {
      setStep((step) => step - 1);
    }
    await validateStep(step);
  };

  const goToNextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (step <= totalSteps - 1) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const goToEditScreen = (
    e: React.MouseEvent<HTMLButtonElement>,
    _cb?: () => void
  ) => {
    e.preventDefault();

    const url = `../edit/${editRouteLink}/${props?.recordName}`;

    navigate(
      replaceLastBreadcrumb({
        path: url,
        label:
          (recordData?.label || recordData?.title || recordData?.name) + '',
      })
    );
  };

  useEffect(() => {
    if (recordData && Object.keys(recordData).length > 0) {
      const enablePadding = !mode;

      const layoutConfig: LayoutConfiguration = {
        pageTitle: enablePadding ? pageTitle : '',
        enablePadding: false,
        headerTitle: enablePadding
          ? (view?.label ?? '')
          : (((recordData?.title as string) ||
              (recordData?.label as string) ||
              viewConfig?.view[0].label) ??
            ''),
        showHeadingLoader: enablePadding ? undefined : false,
      };

      configureLayout(layoutConfig);
    }
  }, [objectDefinition, recordData]);

  const fetchRecordDetailsAPI = async (version: string | undefined) => {
    const response = await getRecordDetailApi(
      objectName,
      props.recordName!,
      version
        ? {
            version: version,
          }
        : {}
    );
    setRecordData(response);
    setShouldRefetchTask(true);
  };

  const validateAllSections = async () => {
    const sectionsFields = sections?.flatMap((section) => section.fields);
    const fieldsNames = sectionsFields?.map((field) => field.columnName);

    let isAllSectionsValid = false;
    if (fieldsNames && fieldsNames.length > 0) {
      isAllSectionsValid = await trigger(fieldsNames);
    }
    setIsAllSectionsValid(isAllSectionsValid);

    //clear errors if the current wizard step's isValid is undefined
    if (wizardStepsState[step]?.isValid === undefined) {
      clearErrors(sections?.[step]?.fields?.map((field) => field.columnName));
    }
  };

  const validateDirtyFields = async () => {
    if (errors && !isEmpty(errors)) {
      await trigger(dirtyFields);
    }
  };

  useEffect(() => {
    setWizardStepsState((prevSteps) =>
      prevSteps.map((prevStep) =>
        prevStep.number === step ? { ...prevStep, isDirty } : prevStep
      )
    );
    const validate = async () => {
      await validateAllSections();
      await validateDirtyFields();
    };

    validate();
  }, [schemaStringified, formDataStringified, step]);

  useEffect(() => {
    const handleSectionErrors = (sections: number[]) => {
      setWizardStepsState((prev) =>
        prev.map((prevStep) =>
          sections.includes(prevStep.number)
            ? { ...prevStep, isValid: false }
            : prevStep
        )
      );
    };

    const handleFieldErrors = (errors: FieldApiError[]) => {
      errors.forEach((error) => {
        setError(error.field, {
          type: 'server side',
          message: error.message,
        });
      });
    };

    const timeoutId = setTimeout(() => {
      if (fieldsApiErrors.sections) {
        handleSectionErrors(fieldsApiErrors.sections);
      }
      if (fieldsApiErrors.fields) {
        handleFieldErrors(fieldsApiErrors.fields);
      }
    }, 1);

    return () => clearTimeout(timeoutId);
  }, [step, fieldsApiErrors]);

  const isWizardStateVisible =
    props.mode !== FormEngineModeTypeEnum.Create &&
    objectDefinition?.isLifecycleEnabled &&
    wizardState !== WizardStateStatus.UNSUBMITTED;

  if (
    isWizardConfigLoading ||
    (isAttributeConfigLoading && !prevObjectDefinition) // show loader only if object definition is not available(first time load)
  ) {
    return (
      <div className={classes.loader}>
        <Loader fullPage />
      </div>
    );
  }

  return (
    <FormEngineContextProvider
      {...{
        fieldsState,
        setFieldsState,
        isWizardStateVisible,
        isCompleterBarVisible: hasTask,
        fetchAttributeConfig: async () => {
          refetchObjectDefinition();
        },
        fieldsApiErrors,
        setFieldsApiErrors,
        setWizardStepsState,
        wizardStepsState,
        outboundRelationships: result,
        onCancel: props.onCancel,
        onSubmit: props.onSubmit,
        isAllSectionsValid,
        objectDefinition,
      }}
    >
      <FormWizardView
        layout={layoutType!}
        viewName={props.viewName}
        objectName={objectName}
        stepsConfig={sections}
        setWizardConfig={setSections}
        attributeConfig={objDefinition}
        submitUrl={submitUrl}
        mode={props.mode}
        recordName={props.recordName}
        isLoaded={isWizardConfigLoading && isAttributeConfigLoading}
        editRoute={editRouteLink}
        step={step}
        setStep={setStep}
        wizardState={wizardState}
        setWizardState={setWizardState}
        totalSteps={totalSteps}
        goToPreviousStep={goToPreviousStep}
        goToNextStep={goToNextStep}
        goToEditScreen={goToEditScreen}
        onStepClick={onStepClick}
        listViewName={listViewName!}
        showCancelPopup={showCancelPopup!}
        labelFormat={labelFormat!}
        viewDto={viewConfig}
        progressTabs={progressTabs}
        recordData={recordData}
        setRecordData={setRecordData}
        methods={methods}
        wizardStepsState={wizardStepsState}
        setWizardStepsState={setWizardStepsState}
        fetchRecordDetailsAPI={fetchRecordDetailsAPI}
        shouldRefetchTask={shouldRefetchTask}
        task={task}
        setTask={setTask}
      />
    </FormEngineContextProvider>
  );
};
